import { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useProjectContext } from "../../context/useProjectContext";
import { useOutletContext } from "react-router-dom";
import { useLicensingContext } from "../../context/useLicensingContext";
import SubscriptionCards from "../SubscriptionsPage/SubscriptionCards";
import Toggle from "../../Components/Generic/Toggle";
import Dropdown from "../../Components/Generic/Dropdown";
import Pill from "../../Components/Generic/Pill";
import { CURRENCY_OPTIONS, CONFERENCES } from "../../constants/OptionsConstants";
import useMemberProductAccess from "../../hooks/useMemberProductAccess";
import usePurchaseSubscription from "../../hooks/usePurchaseSubscription";
import useSortedPagination from "../../hooks/useSortedPagination";
import PageHeader from "../../Components/Layout/PageHeader";
import ProductUsageWidget from "../../Components/Generic/ProductUsageWidget";
import MemberAccessBanner from "../../Components/Generic/MemberAccessBanner";
import { getDefaultMonthToDateRange } from "../../helper/dateFormat";
import AppDateRangePicker from "../../Components/Generic/AppDateRangePicker";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import CustomModal from "../../Components/Generic/CustomModal";
import StyledTable, {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/Generic/StyledTable";
import TablePagination from "../../Components/Generic/TablePagination";
import LoaderComponent from "../../Components/Generic/LoaderComponent";
import CustomInput from "../../Components/Generic/CustomInput";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  getConferenceOrganizersAPICall,
  addConferenceOrganizersAPICall,
  disableConferenceOrganizersAPICall,
  getConferencesListAPICall,
  getConferenceParticipantsBreakdownAPICall,
} from "../../api/licensing/licensingService";
import { showGenericError, showErrorNotification } from "../../helper/Notifications";
import { Eye, Trash2 } from "lucide-react";
import { formatNumber } from "../../helper/numberFormat";

const TAB_CONFERENCES = "conferences";
const TAB_ORGANIZERS = "organizers";

const STATUS_VARIANT = {
  ended: "error",
  scheduled: "warning",
  active: "success",
  invited: "warning",
  inactive: "default",
};

// Per-participant breakdown is disabled until conference usage is tracked
// per participant (pending the EndService usage-recording rework).
const CONFERENCE_BREAKDOWN_ENABLED = false;

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const ConferencesPage = () => {
  const { selectedProject } = useProjectContext();
  const { setShowSalesModal } = useOutletContext();
  const { subscription } = useLicensingContext();
  const {
    handlePurchase,
    annualPricing,
    setAnnualPricing,
    effectiveAnnual,
    billingInterval,
  } = usePurchaseSubscription();
  const { hasProductAccess: hasMemberConferencesAccess, isAdmin } =
    useMemberProductAccess("conferences");

  const [currency, setCurrency] = useState(() => subscription?.currency || "GBP");
  const [activeTab, setActiveTab] = useState(TAB_ORGANIZERS);

  const [showAddOrganizersModal, setShowAddOrganizersModal] = useState(false);
  const [organizerEmailInput, setOrganizerEmailInput] = useState("");

  const [confItemsPerPage, setConfItemsPerPage] = useState(20);
  const [orgItemsPerPage, setOrgItemsPerPage] = useState(20);

  const [conferences, setConferences] = useState([]);
  const [conferencesLoading, setConferencesLoading] = useState(false);
  const confPagination = useSortedPagination({ initialSortBy: "call_date", initialAscending: false });
  const [dateRange, setDateRange] = useState(getDefaultMonthToDateRange);
  const [confSearchTerm, setConfSearchTerm] = useState("");
  const debouncedConfSearchTerm = useDebouncedValue(confSearchTerm.trim(), 300);
  const [confStatusFilter, setConfStatusFilter] = useState("all");

  const [organizers, setOrganizers] = useState([]);
  const [organizersLoading, setOrganizersLoading] = useState(false);
  const [organizerToRemove, setOrganizerToRemove] = useState(null);
  const orgPagination = useSortedPagination({ initialSortBy: "organizer", initialAscending: true });
  const [orgStatusFilter, setOrgStatusFilter] = useState("active");
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const debouncedOrgSearchTerm = useDebouncedValue(orgSearchTerm.trim(), 300);

  const [breakdownConference, setBreakdownConference] = useState(null);
  const [breakdownParticipants, setBreakdownParticipants] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const conferencesItem = subscription?.items?.find(
    (i) => i.product_slug === "conferences"
  );
  const hasConferences = !!conferencesItem || hasMemberConferencesAccess;

  useEffect(() => {
    if (subscription?.currency) setCurrency(subscription.currency);
  }, [subscription]);

  const loadConferences = useCallback(() => {
    if (!selectedProject || !hasConferences) return;
    setConferencesLoading(true);
    const [rangeStart, rangeEnd] = dateRange || [];
    getConferencesListAPICall(
      selectedProject.id,
      confPagination.page,
      confItemsPerPage,
      confPagination.sortBy,
      confPagination.ascending,
      (res) => {
        setConferences(res.data.conferences || []);
        confPagination.setTotalPages(res.data.pagination?.total_pages || 1);
        setConferencesLoading(false);
      },
      () => {
        setConferencesLoading(false);
      },
      rangeStart ? new Date(rangeStart).toISOString() : undefined,
      rangeEnd ? new Date(rangeEnd).toISOString() : undefined,
      debouncedConfSearchTerm,
      confStatusFilter
    );
  }, [selectedProject, hasConferences, confPagination.page, confItemsPerPage, confPagination.sortBy, confPagination.ascending, dateRange, debouncedConfSearchTerm, confStatusFilter]);

  const loadOrganizers = useCallback(() => {
    if (!selectedProject || !hasConferences) return;
    setOrganizersLoading(true);
    const [rangeStart, rangeEnd] = dateRange || [];
    getConferenceOrganizersAPICall(
      selectedProject.id,
      orgPagination.page,
      orgItemsPerPage,
      orgPagination.sortBy,
      orgPagination.ascending,
      (res) => {
        setOrganizers(res.data.organizers || []);
        orgPagination.setTotalPages(res.data.pagination?.total_pages || 1);
        setOrganizersLoading(false);
      },
      () => {
        setOrganizersLoading(false);
      },
      orgStatusFilter,
      {
        start_date: rangeStart ? new Date(rangeStart).toISOString() : undefined,
        end_date: rangeEnd ? new Date(rangeEnd).toISOString() : undefined,
      },
      debouncedOrgSearchTerm
    );
  }, [selectedProject, hasConferences, orgPagination.page, orgItemsPerPage, orgPagination.sortBy, orgPagination.ascending, orgStatusFilter, dateRange, debouncedOrgSearchTerm]);

  useEffect(() => {
    if (activeTab === TAB_CONFERENCES) loadConferences();
    if (activeTab === TAB_ORGANIZERS) loadOrganizers();
  }, [activeTab, loadConferences, loadOrganizers]);

  const handleAddOrganizers = () => {
    if (!organizerEmailInput.trim() || !selectedProject)
      return Promise.reject(new Error("Missing input"));

    const emailArray = Array.from(
      new Set(
        organizerEmailInput
          .replace(/\n/g, ",")
          .replace(/\s+/g, "")
          .split(",")
          .map((e) => e.toLowerCase())
          .filter(Boolean)
      )
    );

    return new Promise((resolve, reject) => {
      addConferenceOrganizersAPICall(
        selectedProject.id,
        emailArray,
        (res) => {
          setOrganizerEmailInput("");
          loadOrganizers();
          resolve(res);
        },
        (err) => {
          const users = err?.response?.data?.users || [];
          const usersList = users.length > 0 ? ` ${users.join(", ")}` : "";
          if (
            err?.response?.data?.message ===
            "Can not add users, they are already enabled in this or other projects"
          ) {
            showErrorNotification(
              `Can not add users, they are already enabled in this or other subscriptions.${usersList}`
            );
          } else {
            showGenericError();
          }
          reject(err);
        }
      );
    });
  };

  const handleRemoveOrganizer = () => {
    if (!organizerToRemove || !selectedProject)
      return Promise.reject(new Error("Missing organizer"));

    return new Promise((resolve, reject) => {
      disableConferenceOrganizersAPICall(
        selectedProject.id,
        [organizerToRemove.email],
        (res) => {
          loadOrganizers();
          resolve(res);
        },
        (err) => {
          showGenericError();
          reject(err);
        }
      );
    });
  };

  const openBreakdown = (conference) => {
    setBreakdownConference(conference);
    setBreakdownParticipants([]);
    setBreakdownLoading(true);
    getConferenceParticipantsBreakdownAPICall(
      selectedProject.id,
      conference.id,
      (res) => {
        setBreakdownParticipants(res.data.participants || []);
        setBreakdownLoading(false);
      },
      () => {
        setBreakdownLoading(false);
      }
    );
  };

  if (subscription === undefined) return null;

  return (
    <Container>
      <CustomModal
        isOpen={showAddOrganizersModal}
        onOpenChange={() => {
          setShowAddOrganizersModal(false);
          setOrganizerEmailInput("");
        }}
        iconType="userPlus"
        iconAlignment="left"
        title="Add Organizers"
        titleAlignment="left"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          {
            text: "Cancel",
            onClick: () => {
              setShowAddOrganizersModal(false);
              setOrganizerEmailInput("");
            },
            variant: "secondary",
          },
          {
            text: "Send Invites",
            action: handleAddOrganizers,
            variant: "primary",
            disabled: !organizerEmailInput.trim(),
            successMessage: "Invites sent successfully",
          },
        ]}
      >
        <ModalContent>
          <ModalDescription>
            Enter one or more email addresses, separated by commas. Each user
            will be invited as a conference organizer.
          </ModalDescription>
          <EmailFieldLabel>Email address</EmailFieldLabel>
          <EmailTextArea
            placeholder="e.g. john@company.com, maria@company.com"
            value={organizerEmailInput}
            onChange={(e) => setOrganizerEmailInput(e.target.value)}
            rows={4}
          />
        </ModalContent>
      </CustomModal>

      <CustomModal
        isOpen={!!breakdownConference}
        onOpenChange={() => setBreakdownConference(null)}
        title={breakdownConference?.name || "Participant Breakdown"}
        titleAlignment="left"
        buttonLayout="row"
        showCloseButton={true}
        width={600}
        buttons={[
          {
            text: "Close",
            onClick: () => setBreakdownConference(null),
            variant: "secondary",
          },
        ]}
      >
        {breakdownLoading ? (
          <LoaderComponent loading={true} />
        ) : breakdownParticipants.length === 0 ? (
          <EmptyState>No participant data available for this conference.</EmptyState>
        ) : (
          <StyledTable>
            <TableHeader>
              <TableRow>
                <TableHead style={{ textAlign: "left", width: "28%" }}>Participant</TableHead>
                <TableHead style={{ textAlign: "center" }}>Role</TableHead>
                <TableHead style={{ textAlign: "center" }}>Audio (min)</TableHead>
                <TableHead style={{ textAlign: "center" }}>Video (min)</TableHead>
                <TableHead style={{ textAlign: "center" }}>Tokens</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {breakdownParticipants.map((p) => (
                <TableRow key={p.user_id}>
                  <TableCell>
                    <ParticipantInfo>
                      <ParticipantName>{p.name || "—"}</ParticipantName>
                      <ParticipantEmail>{p.email || ""}</ParticipantEmail>
                    </ParticipantInfo>
                  </TableCell>
                  <TableCell style={{ textAlign: "center" }}>
                    <RolePill role={p.role}>{p.role || "attendee"}</RolePill>
                  </TableCell>
                  <TableCell style={{ textAlign: "center" }}>{formatNumber(p.audio_minutes, 1)}</TableCell>
                  <TableCell style={{ textAlign: "center" }}>{formatNumber(p.video_minutes, 1)}</TableCell>
                  <TableCell style={{ textAlign: "center" }}>{formatNumber(p.tokens, 1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        )}
      </CustomModal>

      <CustomModal
        isOpen={!!organizerToRemove}
        onOpenChange={() => setOrganizerToRemove(null)}
        iconType="warning"
        iconAlignment="left"
        title="Remove organizer?"
        titleAlignment="left"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          {
            text: "Cancel",
            onClick: () => setOrganizerToRemove(null),
            variant: "secondary",
          },
          {
            text: "Remove",
            action: handleRemoveOrganizer,
            variant: "delete",
            successMessage: "Organizer removed successfully",
          },
        ]}
      >
        <ModalContent>
          <ModalDescription>
            {organizerToRemove?.name || organizerToRemove?.email} will lose
            conference access. This does not delete their account and does not
            affect billing.
          </ModalDescription>
        </ModalContent>
      </CustomModal>

      <PageContent>
        <Header>
          <HeaderContent>
            <PageHeader
              title={hasConferences ? CONFERENCES : `${CONFERENCES} Plans`}
              subtitle={
                hasConferences
                  ? "Add organizers to give teammates access to our conferences platform across web and desktop."
                  : "Choose a plan to unlock real-time AI interpretation for your conferences."
              }
            />
          </HeaderContent>
          <HeaderRight>
            {!hasConferences && (
              <Dropdown
                value={currency}
                onValueChange={setCurrency}
                size="default"
                options={CURRENCY_OPTIONS}
                disabled={!!billingInterval}
              />
            )}
            {hasConferences && isAdmin && (
              <PrimaryButton
                icon={<AddOrganizersIcon />}
                text="Add Organizers"
                onClick={() => setShowAddOrganizersModal(true)}
                disabled={subscription?.status === "canceled"}
              />
            )}
          </HeaderRight>
        </Header>

        {hasConferences && <MemberAccessBanner seatBased />}

        {hasConferences && selectedProject && (
          <>
            <ProductUsageWidget
              projectId={selectedProject.id}
              productSlug="conferences"
              dateRange={dateRange}
              calendarAction={
                <AppDateRangePicker
                  value={dateRange}
                  onChange={(newRange) => { setDateRange(newRange); confPagination.setPage(1); }}
                />
              }
            />

            <TabBar>
              <TabButton
                aria-selected={activeTab === TAB_ORGANIZERS}
                onClick={() => setActiveTab(TAB_ORGANIZERS)}
              >
                Organizers
              </TabButton>
              <TabButton
                aria-selected={activeTab === TAB_CONFERENCES}
                onClick={() => setActiveTab(TAB_CONFERENCES)}
              >
                Conferences
              </TabButton>
            </TabBar>

            {activeTab === TAB_ORGANIZERS && (
              <OrgFiltersRow>
                <CustomInput
                  value={orgSearchTerm}
                  onChange={(e) => { setOrgSearchTerm(e.currentTarget.value); orgPagination.setPage(1); }}
                  placeholder="Search organizers..."
                  wrapperStyle={{ maxWidth: "280px" }}
                  className="h-9 py-1.5"
                />
                <div style={{ minWidth: 160 }}>
                  <Dropdown
                    value={orgStatusFilter}
                    onValueChange={(v) => { setOrgStatusFilter(v); orgPagination.setPage(1); }}
                    placeholder="All statuses"
                    className="h-9 py-1.5"
                    options={[
                      { value: "all", label: "All statuses" },
                      { value: "active", label: "Active" },
                      { value: "invited", label: "Invite sent" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                  />
                </div>
              </OrgFiltersRow>
            )}

            {activeTab === TAB_CONFERENCES && (
              <OrgFiltersRow>
                <CustomInput
                  value={confSearchTerm}
                  onChange={(e) => { setConfSearchTerm(e.currentTarget.value); confPagination.setPage(1); }}
                  placeholder="Search conferences..."
                  wrapperStyle={{ maxWidth: "280px" }}
                  className="h-9 py-1.5"
                />
                <div style={{ minWidth: 160 }}>
                  <Dropdown
                    value={confStatusFilter}
                    onValueChange={(v) => { setConfStatusFilter(v); confPagination.setPage(1); }}
                    placeholder="All statuses"
                    className="h-9 py-1.5"
                    options={[
                      { value: "all", label: "All statuses" },
                      { value: "scheduled", label: "Scheduled" },
                      { value: "ended", label: "Ended" },
                    ]}
                  />
                </div>
              </OrgFiltersRow>
            )}

            {activeTab === TAB_CONFERENCES && (
              conferencesLoading ? (
                <LoaderComponent loading={true} />
              ) : conferences.length === 0 ? (
                <EmptyState>No conferences found for this project.</EmptyState>
              ) : (
                <StyledTable>
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ textAlign: "left", width: "20%" }}>
                        <SortButton onClick={() => confPagination.toggleSort("name")} active={confPagination.sortBy === "name"}>
                          Conference{confPagination.sortArrow("name")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => confPagination.toggleSort("organizers")} active={confPagination.sortBy === "organizers"}>
                          Organizer(s){confPagination.sortArrow("organizers")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => confPagination.toggleSort("call_date")} active={confPagination.sortBy === "call_date"}>
                          Date{confPagination.sortArrow("call_date")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => confPagination.toggleSort("audio_minutes")} active={confPagination.sortBy === "audio_minutes"}>
                          Audio min{confPagination.sortArrow("audio_minutes")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => confPagination.toggleSort("video_minutes")} active={confPagination.sortBy === "video_minutes"}>
                          Video min{confPagination.sortArrow("video_minutes")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => confPagination.toggleSort("tokens")} active={confPagination.sortBy === "tokens"}>
                          Words{confPagination.sortArrow("tokens")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => confPagination.toggleSort("status")} active={confPagination.sortBy === "status"}>
                          Status{confPagination.sortArrow("status")}
                        </SortButton>
                      </TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conferences.map((conf) => (
                      <TableRow key={conf.id}>
                        <TableCell>
                          <ConferenceName>{conf.name}</ConferenceName>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {conf.organizers?.length > 0 ? (
                            <OrganizersList>
                              {conf.organizers.map((o) => (
                                <ParticipantInfo key={o.id}>
                                  <ParticipantName>{o.name || o.email}</ParticipantName>
                                  {o.name && <ParticipantEmail>{o.email}</ParticipantEmail>}
                                </ParticipantInfo>
                              ))}
                            </OrganizersList>
                          ) : "—"}
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>{formatDate(conf.call_date)}</TableCell>
                        <TableCell style={{ textAlign: "center" }}>{formatNumber(conf.audio_minutes, 1)}</TableCell>
                        <TableCell style={{ textAlign: "center" }}>{formatNumber(conf.video_minutes, 1)}</TableCell>
                        <TableCell style={{ textAlign: "center" }}>{formatNumber(conf.tokens, 1)}</TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <Pill variant={STATUS_VARIANT[conf.status]} size="sm" className="capitalize">{conf.status}</Pill>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          {conf.has_call && CONFERENCE_BREAKDOWN_ENABLED && (
                            <EyeButton
                              onClick={() => openBreakdown(conf)}
                              title="View participant breakdown"
                            >
                              <Eye size={16} />
                            </EyeButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TablePagination
                    page={confPagination.page}
                    totalPages={confPagination.totalPages}
                    onPrevious={confPagination.goToPrevious}
                    onNext={confPagination.goToNext}
                    leftColSpan={3}
                    centerColSpan={2}
                    rightColSpan={3}
                    itemsPerPage={confItemsPerPage}
                    onItemsPerPageChange={(v) => { setConfItemsPerPage(v); confPagination.setPage(1); }}
                  />
                </StyledTable>
              )
            )}

            {activeTab === TAB_ORGANIZERS && (
              organizersLoading ? (
                <LoaderComponent loading={true} />
              ) : organizers.length === 0 ? (
                <EmptyState>
                  {orgStatusFilter !== "all" ? (
                    "No organizers match this status."
                  ) : (
                    <>
                      No organizers added yet.{" "}
                      {isAdmin && (
                        <AddOrgLink onClick={() => setShowAddOrganizersModal(true)}>
                          Add organizers
                        </AddOrgLink>
                      )}
                    </>
                  )}
                </EmptyState>
              ) : (
                <StyledTable>
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ textAlign: "left", width: "24%" }}>
                        <SortButton onClick={() => orgPagination.toggleSort("organizer")} active={orgPagination.sortBy === "organizer"}>
                          Organizer{orgPagination.sortArrow("organizer")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => orgPagination.toggleSort("status")} active={orgPagination.sortBy === "status"}>
                          Status{orgPagination.sortArrow("status")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => orgPagination.toggleSort("audio_minutes")} active={orgPagination.sortBy === "audio_minutes"}>
                          Audio min{orgPagination.sortArrow("audio_minutes")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => orgPagination.toggleSort("video_minutes")} active={orgPagination.sortBy === "video_minutes"}>
                          Video min{orgPagination.sortArrow("video_minutes")}
                        </SortButton>
                      </TableHead>
                      <TableHead style={{ textAlign: "center" }}>
                        <SortButton onClick={() => orgPagination.toggleSort("tokens")} active={orgPagination.sortBy === "tokens"}>
                          Words{orgPagination.sortArrow("tokens")}
                        </SortButton>
                      </TableHead>
                      {isAdmin && <TableHead style={{ textAlign: "center" }}>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizers.map((org) => (
                      <TableRow key={org.id}>
                        <TableCell>
                          <ParticipantInfo>
                            <ParticipantName>{org.name || org.email}</ParticipantName>
                            {org.name && <ParticipantEmail>{org.email}</ParticipantEmail>}
                          </ParticipantInfo>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <Pill variant={STATUS_VARIANT[org.status]} size="sm">
                            {org.status === "active" ? "Active" : org.status === "inactive" ? "Inactive" : "Invite sent"}
                          </Pill>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>{formatNumber(org.audio_minutes, 1)}</TableCell>
                        <TableCell style={{ textAlign: "center" }}>{formatNumber(org.video_minutes, 1)}</TableCell>
                        <TableCell style={{ textAlign: "center" }}>{formatNumber(org.tokens, 1)}</TableCell>
                        {isAdmin && (
                          <TableCell style={{ textAlign: "center" }}>
                            <RemoveButton
                              onClick={() => setOrganizerToRemove(org)}
                              disabled={org.removable === false}
                              title={org.removable === false ? "This organizer can't be removed" : "Remove organizer"}
                            >
                              <Trash2 size={16} />
                            </RemoveButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                  <TablePagination
                    page={orgPagination.page}
                    totalPages={orgPagination.totalPages}
                    onPrevious={orgPagination.goToPrevious}
                    onNext={orgPagination.goToNext}
                    leftColSpan={2}
                    centerColSpan={isAdmin ? 2 : 1}
                    rightColSpan={2}
                    itemsPerPage={orgItemsPerPage}
                    onItemsPerPageChange={(v) => { setOrgItemsPerPage(v); orgPagination.setPage(1); }}
                  />
                </StyledTable>
              )
            )}
          </>
        )}

        {!hasConferences && (
          <PlanCardsSection>
            <PlanCardsHeader>
              <PricingLabel>Pricing plans</PricingLabel>
              {!billingInterval && (
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <Toggle
                    checked={annualPricing}
                    onCheckedChange={setAnnualPricing}
                  />
                  <span className="font-inter text-[16px] leading-6 font-medium text-brand-900">
                    Annual pricing{" "}
                    <span className="text-brand-700 font-medium">(save 10%)</span>
                  </span>
                </label>
              )}
            </PlanCardsHeader>
            <SubscriptionCards
              purchaseSubscription={handlePurchase}
              contactSales={() => setShowSalesModal(true)}
              annual={effectiveAnnual}
              currency={currency}
              product="conferences"
            />
          </PlanCardsSection>
        )}
      </PageContent>
    </Container>
  );
};

export default ConferencesPage;

const AddOrganizersIcon = (props) => (
  <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M14.1666 16.5V14.8333C14.1666 13.9493 13.8155 13.1014 13.1903 12.4763C12.5652 11.8512 11.7174 11.5 10.8333 11.5H4.16665C3.28259 11.5 2.43474 11.8512 1.80962 12.4763C1.1845 13.1014 0.833313 13.9493 0.833313 14.8333V16.5M19.1666 16.5V14.8333C19.1661 14.0948 18.9203 13.3773 18.4678 12.7936C18.0153 12.2099 17.3818 11.793 16.6666 11.6083M13.3333 1.60833C14.0503 1.79192 14.6858 2.20892 15.1397 2.79359C15.5935 3.37827 15.8398 4.09736 15.8398 4.8375C15.8398 5.57764 15.5935 6.29673 15.1397 6.88141C14.6858 7.46608 14.0503 7.88308 13.3333 8.06667M10.8333 4.83333C10.8333 6.67428 9.34093 8.16667 7.49998 8.16667C5.65903 8.16667 4.16665 6.67428 4.16665 4.83333C4.16665 2.99238 5.65903 1.5 7.49998 1.5C9.34093 1.5 10.8333 2.99238 10.8333 4.83333Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding: 30px 30px 30px 30px;
  height: 100%;
  background: #f9fafb;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: white;
  margin: -40px -30px 0 -30px;
  padding: 30px 30px 10px 30px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const TabBar = styled.div`
  display: inline-flex;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
  align-self: flex-start;
`;

const OrgFiltersRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  border: none;
  background: transparent;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease;

  &[aria-selected="true"] {
    background: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
    color: #111827;
    font-weight: 600;
  }
`;

const EmptyState = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 40px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const AddOrgLink = styled.span`
  color: #6941c6;
  font-weight: 600;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const ConferenceName = styled.div`
  font-weight: 500;
  color: #111827;
  font-size: 14px;
`;

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: inherit;
  font-weight: ${(p) => (p.active ? "700" : "600")};
  color: ${(p) => (p.active ? "#111827" : "#374151")};
  padding: 0;
  white-space: nowrap;
`;

const RolePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${({ role }) =>
    role === "organizer" ? "#ede9fe" :
    role === "presenter" ? "#dbeafe" :
    "#f3f4f6"};
  color: ${({ role }) =>
    role === "organizer" ? "#5b21b6" :
    role === "presenter" ? "#1d4ed8" :
    "#374151"};
`;

const EyeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #9ca3af;
    color: #374151;
    background: #f9fafb;
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #fca5a5;
    color: #dc2626;
    background: #fef2f2;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    color: #6b7280;
    background: white;
    border-color: #e5e7eb;
  }
`;

const OrganizersList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const ParticipantInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ParticipantName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: #111827;
`;

const ParticipantEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalDescription = styled.p`
  color: #4b5563;
  font-size: 14px;
  line-height: 20px;
  margin: 0;
`;

const EmailFieldLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const EmailTextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  resize: vertical;
  font-family: inherit;
  outline: none;

  &:focus {
    border-color: #6941c6;
    box-shadow: 0 0 0 3px rgba(105, 65, 198, 0.1);
  }
`;

const PlanCardsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const PlanCardsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const PricingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 2px 12px;
  border-radius: 16px;
  background: #F4EBFF;
  color: #6941C6;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;
